import Layer from '@/scripts/canvas/Layer'
import { delay } from '@/scripts/helpers/animation'
import { getRandomInt } from '@/scripts/utils/math'
import { createVehicleWithLight, flipVehicle } from './vehicleFactory'
import { animateCar, animateTram } from './vehicleAnimations'
import { fadeInVehicleSound, fadeOutVehicleSound } from '@/scripts/audio/soundController'

type VehiclePoint = { id: string; x: number; y: number }

export type TrafficConfig = {
  passenger: { vehicle?: VehiclePoint; light?: VehiclePoint; wheel?: VehiclePoint; direction: 'left' | 'right' }
  truck: {
    vehicle?: VehiclePoint
    light?: VehiclePoint
    wheel?: VehiclePoint
    sign?: VehiclePoint
    direction: 'left' | 'right'
  }
  tram: { vehicle?: VehiclePoint; light?: VehiclePoint; direction: 'left' | 'right' }
  travelDistance: number
  passengerCarDriveDuration: number
  truckCarDriveDuration: number
  tramDriveDuration: number
  pauseAfterPassengerCar: number
  pauseAfterTram: number
  pauseBeforeLoop: number
  tramStopHold: number
}

export async function startTrafficSequence(layer: Layer, config: TrafficConfig): Promise<void> {
  const {
    passenger,
    truck,
    tram,
    travelDistance,
    passengerCarDriveDuration,
    truckCarDriveDuration,
    tramDriveDuration,
    tramStopHold,
  } = config

  if (!passenger.vehicle || !truck.vehicle || !tram.vehicle) return

  const passengerSprite = createVehicleWithLight(
    layer,
    passenger.vehicle!,
    passenger.light,
    [passenger.wheel!, passenger.wheel!],
    undefined,
    {
      wheelOffset: [
        { x: 13, y: 13, rotation: 0 }, // front wheel
        { x: 95, y: 13, rotation: 0.5 }, // rear wheel
      ],
    },
  )

  const truckSprite = createVehicleWithLight(
    layer,
    truck.vehicle!,
    truck.light,
    [truck.wheel!, truck.wheel!],
    truck.sign,
    {
      wheelOffset: [
        { x: 20, y: 18, rotation: 0.2 }, // front wheel
        { x: 140, y: 18, rotation: -0.3 }, // rear wheel
      ],
      signOffset: { x: 0, y: 0 },
    },
  )

  const tramSprite = createVehicleWithLight(layer, tram.vehicle!, tram.light, undefined, undefined, undefined)

  const animateTramVehicle = async () => {
    const startX = tram.vehicle!.x
    const direction = tram.direction
    const tramStopPosition = 1300
    const exitX = direction === 'right' ? startX + travelDistance : startX - travelDistance

    const totalDistance = travelDistance
    const distanceToStop = Math.abs(tramStopPosition - startX)
    const distanceToExit = Math.abs(exitX - tramStopPosition)

    // Mirror animateTram timing
    const leg1DurationSec = Math.max(
      0.1,
      Math.min(tramDriveDuration, (distanceToStop / totalDistance) * tramDriveDuration),
    )
    const leg2DurationSec = Math.max(
      0.1,
      Math.min(tramDriveDuration, (distanceToExit / totalDistance) * tramDriveDuration),
    )

    // When tram starts driving: wait, then fade in
    ;(async () => {
      await delay(2)
      fadeInVehicleSound('tramvaj_prijezd', 500)
    })()

    // Fade out while it is approaching the stop
    ;(async () => {
      fadeOutVehicleSound('tramvaj_prijezd', 500)
    })()

    // When it starts driving again from the stop, add a delay before playing, then fade in
    ;(async () => {
      const fadeTotalMs = 6000
      const fadeTotalSec = fadeTotalMs / 1000
      const actualFadeSec = Math.min(fadeTotalSec, leg2DurationSec)

      await delay(leg1DurationSec + tramStopHold + 0.5)
      fadeInVehicleSound('tramvaj_odjezd', actualFadeSec * 0)
    })()

    // Final fade out
    ;(async () => {
      const fadeTotalMs = 2000
      const fadeTotalSec = fadeTotalMs / 1000

      const totalSec = leg1DurationSec + tramStopHold + leg2DurationSec

      const startTimeSec = Math.max(0, totalSec - fadeTotalSec)
      const actualFadeSec = Math.min(fadeTotalSec, leg2DurationSec)

      await delay(startTimeSec)
      fadeOutVehicleSound('tramvaj_odjezd', actualFadeSec * 1000)
    })()

    await animateTram(tramSprite, startX, direction, travelDistance, tramDriveDuration, tramStopHold)
  }

  const animatePassengerCar = async (direction: 'left' | 'right' = 'right') => {
    passengerSprite.zIndex = direction === 'left' ? 8 : 10
    flipVehicle(passengerSprite, direction === 'left', false)
    passengerSprite.y = passenger.vehicle!.y + (direction === 'left' ? -5 : 5)
    ;(async () => {
      fadeInVehicleSound('auto', 2000)
    })()

    // Start fading out before nominal end of drive
    ;(async () => {
      const approxDriveDurationSec = passengerCarDriveDuration
      const waitSec = Math.max(0, approxDriveDurationSec - 3)
      await delay(waitSec)
      fadeOutVehicleSound('auto', 2000)
    })()

    await animateCar(
      passengerSprite,
      passenger.vehicle!.x + (direction === 'left' ? travelDistance : 0),
      direction,
      travelDistance,
      passengerCarDriveDuration,
    )
  }

  const animateTruckCar = async (direction: 'left' | 'right' = 'left') => {
    truckSprite.zIndex = direction === 'left' ? 8 : 10
    flipVehicle(truckSprite, direction === 'right', true)
    truckSprite.y = truck.vehicle!.y + (direction === 'left' ? -5 : 5)
    ;(async () => {
      await delay(1)
      fadeInVehicleSound('nakladak', 2000)
    })()

    // Start fading out before nominal end of drive
    ;(async () => {
      const approxDriveDurationSec = truckCarDriveDuration // base (7s)
      const waitSec = Math.max(0, approxDriveDurationSec - 2)
      await delay(waitSec)
      fadeOutVehicleSound('nakladak', 2000)
    })()

    await animateCar(
      truckSprite,
      truck.vehicle!.x - (direction === 'right' ? travelDistance : 0),
      direction,
      travelDistance,
      truckCarDriveDuration,
    )
  }

  const tramLoop = async () => {
    await delay(4)
    await animateTramVehicle()
    await delay(getRandomInt(20, 30))
    tramLoop()
  }

  const passengerCarLoop = async () => {
    await animatePassengerCar('left')
    await delay(getRandomInt(15, 25))
    await animatePassengerCar('right')
    await delay(getRandomInt(15, 25))
    passengerCarLoop()
  }

  const truckCarLoop = async () => {
    await animateTruckCar('right')
    await delay(getRandomInt(15, 25))
    await animateTruckCar('left')
    await delay(getRandomInt(15, 25))
    truckCarLoop()
  }

  const loop = async () => {
    tramLoop()
    passengerCarLoop()
    truckCarLoop()
  }

  loop()
}
